import apiClient from './client';

// ─── Request types ───────────────────────────────────────────────────────────

export interface GetTimesheetParams {
  startDate: string;
  endDate: string;
  groupBy: 'user' | 'board' | 'item' | 'board_summary' | 'board_user';
  userIds?: string[];
  boardIds?: string[];
  subGroupBy?: 'category';
}

export interface GetCapacitiesParams {
  startDate: string;
  endDate: string;
  userIds: string[];
}

// ─── Response types ──────────────────────────────────────────────────────────

export interface TimesheetAggRow {
  groupKey: string;
  childKey: string | null;
  date: string;
  hours: number;
  worklogCount: number;
  billableHours: number;
  categoryId: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  categoryIsBillable: boolean | null;
}

export interface TimesheetResponse {
  startDate: string;
  endDate: string;
  groupBy: string;
  rows: TimesheetAggRow[];
}

export interface UserDayCapacity {
  userId: string;
  date: string;
  capacityHours: number;
  isHoliday: boolean;
  isWeekend: boolean;
  isLeave: boolean;
  availableHours: number;
}

export interface TimesheetCapacityResponse {
  startDate: string;
  endDate: string;
  capacities: UserDayCapacity[];
}

// ─── Normalizers ─────────────────────────────────────────────────────────────

function normalizeAggRow(raw: Record<string, unknown>): TimesheetAggRow {
  return {
    groupKey: String(raw.groupKey ?? raw.group_key ?? ''),
    childKey: raw.childKey != null
      ? String(raw.childKey)
      : raw.child_key != null
        ? String(raw.child_key)
        : null,
    date: String(raw.date ?? '').slice(0, 10),
    hours: Number(raw.hours ?? 0),
    worklogCount: Number(raw.worklogCount ?? raw.worklog_count ?? 0),
    billableHours: Number(raw.billableHours ?? raw.billable_hours ?? 0),
    categoryId: raw.categoryId != null
      ? String(raw.categoryId)
      : raw.category_id != null
        ? String(raw.category_id)
        : null,
    categoryName: raw.categoryName != null
      ? String(raw.categoryName)
      : raw.category_name != null
        ? String(raw.category_name)
        : null,
    categoryColor: raw.categoryColor != null
      ? String(raw.categoryColor)
      : raw.category_color != null
        ? String(raw.category_color)
        : null,
    categoryIsBillable: raw.categoryIsBillable != null
      ? Boolean(raw.categoryIsBillable)
      : raw.category_is_billable != null
        ? Boolean(raw.category_is_billable)
        : null,
  };
}

function normalizeCapacity(raw: Record<string, unknown>): UserDayCapacity {
  return {
    userId: String(raw.userId ?? raw.user_id ?? ''),
    date: String(raw.date ?? '').slice(0, 10),
    capacityHours: Number(raw.capacityHours ?? raw.capacity_hours ?? 0),
    isHoliday: Boolean(raw.isHoliday ?? raw.is_holiday ?? false),
    isWeekend: Boolean(raw.isWeekend ?? raw.is_weekend ?? false),
    isLeave: Boolean(raw.isLeave ?? raw.is_leave ?? false),
    availableHours: Number(raw.availableHours ?? raw.available_hours ?? 0),
  };
}

// ─── API calls ───────────────────────────────────────────────────────────────

/**
 * Fetch aggregated timesheet data.
 */
export async function getTimesheetTimeline(
  params: GetTimesheetParams,
  signal?: AbortSignal,
): Promise<TimesheetResponse> {
  const queryParams: Record<string, string> = {
    startDate: params.startDate,
    endDate: params.endDate,
    groupBy: params.groupBy,
  };
  if (params.userIds && params.userIds.length > 0) {
    queryParams.userIds = params.userIds.join(',');
  }
  if (params.boardIds && params.boardIds.length > 0) {
    queryParams.boardIds = params.boardIds.join(',');
  }
  if (params.subGroupBy) {
    queryParams.subGroupBy = params.subGroupBy;
  }

  const { data } = await apiClient.get<Record<string, unknown>>(
    '/timesheets/timeline',
    { params: queryParams, signal },
  );

  const rawRows = (data.rows as Record<string, unknown>[]) ?? [];
  return {
    startDate: String(data.startDate ?? params.startDate),
    endDate: String(data.endDate ?? params.endDate),
    groupBy: String(data.groupBy ?? params.groupBy),
    rows: rawRows.map(normalizeAggRow),
  };
}

/**
 * Fetch capacity + holiday + leave info per user per day.
 */
export async function getTimesheetCapacities(
  params: GetCapacitiesParams,
  signal?: AbortSignal,
): Promise<TimesheetCapacityResponse> {
  const { data } = await apiClient.get<Record<string, unknown>>(
    '/timesheets/capacities',
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        userIds: params.userIds.join(','),
      },
      signal,
    },
  );

  const rawCaps = (data.capacities as Record<string, unknown>[]) ?? [];
  return {
    startDate: String(data.startDate ?? params.startDate),
    endDate: String(data.endDate ?? params.endDate),
    capacities: rawCaps.map(normalizeCapacity),
  };
}
